import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, AlertOctagon, Palmtree, ShieldAlert, CalendarClock, UserMinus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { usePortalAnchorRect } from "../lib/usePortalAnchorRect";

const POLL_INTERVAL_MS = 60_000;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  incident:         { icon: AlertOctagon,  label: "Incident" },
  vacation_pending: { icon: Palmtree,      label: "Vacation" },
  vacation_decision:{ icon: Palmtree,      label: "Vacation" },
  cert_expiry:      { icon: ShieldAlert,   label: "Cert" },
  shift_assigned:   { icon: CalendarClock, label: "Shift" },
  coverage_change:  { icon: UserMinus,     label: "Coverage" },
};

const SEV_TINT = {
  high: "text-[#FF3B30]",
  medium: "text-[#FF9F0A]",
  low: "text-zinc-400",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsBell({ collapsed }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [lastReadAt, setLastReadAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  // Anchor rect (viewport coords) — used to position the panel now that it
  // lives in a document.body portal outside the sidebar's stacking context.
  // Recalculated on open + on scroll/resize via the shared hook so the
  // panel tracks the bell if the user scrolls the underlying page.
  const anchorRect = usePortalAnchorRect(open, anchorRef);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { days: 14 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
      setLastReadAt(r.data.last_read_at || null);
    } catch { /* offline / 401 — bell silently hides count */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [load]);

  // Real-time push via Server-Sent Events. On any `notification` event we
  // simply refetch the aggregated list — cheap and keeps the UI honest.
  useEffect(() => {
    if (!API_BASE || typeof window === "undefined" || !window.EventSource) return undefined;
    const es = new EventSource(`${API_BASE}/api/notifications/stream`, { withCredentials: true });
    es.addEventListener("notification", (e) => {
      // Pop a toast for anything HIGH severity so operators never miss it,
      // even with the bell panel closed. Medium+low silently refetch.
      try {
        const data = JSON.parse(e.data || "{}");
        if (data.severity === "high") {
          toast.error(data.title || "Alert", {
            description: data.body,
            action: data.url ? { label: "Open", onClick: () => navigate(data.url) } : undefined,
            duration: 8000,
          });
        }
      } catch (_) { /* malformed — still refetch */ }
      load();
    });
    es.addEventListener("error", () => { /* browser auto-reconnects after `retry:` */ });
    return () => { es.close(); };
  }, [load, navigate]);

  // Close on outside click. The panel now lives in a document.body portal,
  // so the anchor ref alone doesn't contain the clicks that happen inside
  // the panel — we check the panel ref too.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const insideAnchor = anchorRef.current && anchorRef.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideAnchor && !insidePanel) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Track the anchor's viewport rect so the fixed-position panel stays
  // glued to the bell as the user scrolls or resizes the window. Handled
  // by the shared `usePortalAnchorRect` hook (see const declaration above).

  const markAllRead = useCallback(async () => {
    try {
      const r = await api.post("/notifications/mark-read", {});
      setUnread(0);
      setLastReadAt(r.data?.last_read_at || new Date().toISOString());
    } catch { /* best-effort */ }
  }, []);

  const grouped = useMemo(() => items, [items]);

  const openItem = (it) => {
    setOpen(false);
    if (it.url) navigate(it.url);
  };

  return (
    <div ref={anchorRef} className={`relative ${collapsed ? "flex justify-center" : ""}`}>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label={`Notifications ${unread ? `(${unread} unread)` : ""}`}
        className={`relative p-2 text-zinc-400 hover:text-white hover:bg-[#121212] transition-colors ${collapsed ? "" : "w-full flex items-center gap-2 justify-start px-3"}`}
      >
        <Bell className="size-4" />
        {!collapsed && <span className="text-[11px] uppercase tracking-widest">Notifications</span>}
        {unread > 0 && (
          <span
            data-testid="notifications-unread-count"
            className={`absolute ${collapsed ? "top-1 right-1" : "top-1 right-2"} min-w-[16px] h-4 px-1 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && anchorRect && createPortal(
        <div
          ref={panelRef}
          data-testid="notifications-panel"
          className="fixed z-[90] w-[380px] max-h-[560px] flex flex-col bg-[#121212] border border-[#27272A] shadow-2xl"
          style={{
            // Anchor to the RIGHT edge of the bell + a small gap. Clamped
            // to the viewport so the panel never renders off-screen even
            // when the bell sits near the bottom of a tall page.
            top: Math.max(8, Math.min(anchorRect.top, window.innerHeight - 560 - 8)),
            left: Math.min(anchorRect.right + 8, window.innerWidth - 380 - 8),
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A]">
            <div>
              <div className="font-display font-black text-sm uppercase tracking-widest">Notifications</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                {loading ? "Refreshing…" : `${items.length} in last 14 days · ${unread} unread`}
              </div>
            </div>
            <button
              data-testid="notifications-mark-all-read"
              onClick={markAllRead}
              disabled={unread === 0}
              className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Check className="size-3" /> Mark all read
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {grouped.length === 0 && (
              <div className="text-center py-16 text-zinc-500 text-xs scanlines" data-testid="notifications-empty">
                All clear. No new alerts.
              </div>
            )}
            {grouped.map((n) => {
              const meta = TYPE_META[n.type] || { icon: Bell, label: n.type };
              const Icon = meta.icon;
              const isUnread = (n.created_at || "") > (lastReadAt || "");
              return (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.type}`}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-[#1c1c1e] hover:bg-[#1c1c1e] flex gap-3 transition-colors ${isUnread ? "bg-[#161618]" : ""}`}
                >
                  <div className={`shrink-0 mt-0.5 ${SEV_TINT[n.severity] || "text-zinc-400"}`}>
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{meta.label}</div>
                      {isUnread && <div className="size-1.5 rounded-full bg-[#FF3B30]" />}
                      <div className="ml-auto text-[10px] text-zinc-600">{timeAgo(n.created_at)}</div>
                    </div>
                    <div className="text-sm text-zinc-200 truncate mt-0.5">{n.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{n.body}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
